(() => {
  'use strict';

  const ROOT_SELECTOR = "[data-cs-collection-layout='maison-index-object-library']";
  const HEADER_SELECTOR = '.shopify-section-group-header-group';
  const controllers = new WeakMap();

  class CorsailleCollectionChromeOffset {
    constructor(root) {
      this.root = root;
      this.frame = 0;
      this.signal = new AbortController();
      this.observers = [];
      this.modalIsolation = [];
      this.request = this.request.bind(this);
      this.measure = this.measure.bind(this);
      this.syncMobileFacetsModal = this.syncMobileFacetsModal.bind(this);

      // The canonical header and utility rail share one CSS transition clock.
      // No scroll-time geometry read/write loop is needed for this known shell.
      this.cssChrome = Boolean(document.querySelector(HEADER_SELECTOR + ' > [data-cs-header]'));
      if (this.cssChrome) {
        this.root.dataset.csStickyChrome = 'css';
        this.root.style.removeProperty('--cs-collection-sticky-offset');
      } else {
        window.addEventListener('scroll', this.request, { passive: true, signal: this.signal.signal });
        window.addEventListener('resize', this.request, { passive: true, signal: this.signal.signal });
        window.visualViewport?.addEventListener('resize', this.request, { passive: true, signal: this.signal.signal });
        window.visualViewport?.addEventListener('scroll', this.request, { passive: true, signal: this.signal.signal });

        const headerGroups = [...document.querySelectorAll(HEADER_SELECTOR)]
          .filter((node) => node.querySelector('.cs-announcement, .cs-header, .corsaille-header'));

        this.chromeElements = headerGroups.map((node) =>
          node.querySelector('.cs-announcement, .cs-header, .corsaille-header')).filter(Boolean);

        if ('ResizeObserver' in window) {
          // Read the animated visible edge in the same rendering cycle. The section
          // wrapper deliberately keeps its height so scroll anchoring cannot reverse scroll direction.
          const resize = new ResizeObserver(this.measure);
          this.chromeElements.forEach((node) => resize.observe(node));
          this.observers.push(resize);
        }

        const mutation = new MutationObserver(this.request);
        headerGroups.forEach((node) => mutation.observe(node, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] }));
        this.observers.push(mutation);

      }

      this.mobileFacetsDialog = root.querySelector('[data-cs-mobile-facets-dialog]');
      this.mobileFacetsFocus = root.querySelector('[data-cs-mobile-facets-dialog-focus]');
      if (this.mobileFacetsDialog) {
        const facetsMutation = new MutationObserver(this.syncMobileFacetsModal);
        facetsMutation.observe(this.mobileFacetsDialog, { attributes: true, attributeFilter: ['open'] });
        this.observers.push(facetsMutation);
      }
      if (!this.cssChrome) this.request();
    }

    syncMobileFacetsModal() {
      if (!this.mobileFacetsDialog?.hasAttribute('open') || !window.matchMedia('(max-width: 989px)').matches) {
        this.restoreModalIsolation();
        return;
      }
      if (this.modalIsolation.length) return;

      let branch = this.mobileFacetsDialog;
      while (branch?.parentElement && branch !== document.body) {
        const parent = branch.parentElement;
        [...parent.children].forEach((sibling) => {
          if (sibling === branch || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) return;
          this.modalIsolation.push({
            element: sibling,
            inert: sibling.getAttribute('inert'),
            ariaHidden: sibling.getAttribute('aria-hidden'),
          });
          sibling.setAttribute('inert', '');
          sibling.setAttribute('aria-hidden', 'true');
        });
        branch = parent;
      }

      window.requestAnimationFrame(() => {
        if (this.mobileFacetsDialog?.hasAttribute('open')) {
          this.mobileFacetsFocus?.focus({ preventScroll: true });
        }
      });
    }

    restoreModalIsolation() {
      this.modalIsolation.forEach(({ element, inert, ariaHidden }) => {
        if (inert == null) element.removeAttribute('inert');
        else element.setAttribute('inert', inert);
        if (ariaHidden == null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      this.modalIsolation = [];
    }

    request() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(this.measure);
    }

    measure() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      if (!this.root.isConnected) return;

      let stackBottom = 0;
      const chromeElements = [...document.querySelectorAll(HEADER_SELECTOR)]
        .map((node) => node.querySelector('.cs-announcement, .cs-header, .corsaille-header')).filter(Boolean);

      chromeElements.forEach((node) => {
        const style = window.getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') return;
        const rect = node.getBoundingClientRect();
        if (rect.height <= 0 || rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        stackBottom = Math.max(stackBottom, rect.bottom);
      });

      if (!chromeElements.length) {
        const styles = window.getComputedStyle(document.documentElement);
        const announcement = Number.parseFloat(styles.getPropertyValue('--cs-announcement-height')) || 26;
        const compressed = Number.parseFloat(styles.getPropertyValue('--cs-header-height-compressed')) || 72;
        stackBottom = announcement + compressed;
      }

      const offset = `${Math.max(0, Math.round(stackBottom * 100) / 100)}px`;
      if (this.root.style.getPropertyValue('--cs-collection-sticky-offset') !== offset) {
        this.root.style.setProperty('--cs-collection-sticky-offset', offset);
      }
    }

    destroy() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.signal.abort();
      this.observers.forEach((observer) => observer.disconnect());
      this.restoreModalIsolation();
      this.root.style.removeProperty('--cs-collection-sticky-offset');
      delete this.root.dataset.csStickyChrome;
    }
  }

  const initialize = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new CorsailleCollectionChromeOffset(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];
    if (event.target.matches?.(ROOT_SELECTOR)) roots.push(event.target);
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => controllers.get(root)?.destroy());
  });
})();
